/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

/**
 * The phone column. One panel that switches on call state.
 *
 * Previously three cards stacked permanently, with the inapplicable ones
 * dimmed to 55% opacity and `pointerEvents: none`. Two problems with that:
 * the agent reads controls that do nothing on the busiest screen in the
 * product, and `pointerEvents` does not stop keyboard focus, so tabbing
 * walked straight into a disabled disposition select and a Save button that
 * silently did nothing.
 *
 * Wrap-up now REPLACES the controls rather than sitting below them, because
 * by then the call is over and hanging up is not an available action. Notes
 * still survive the transition: they are held by the parent, so an agent can
 * start typing mid-call and keep what they wrote.
 */

import React, { useState } from "react";
import {
  Collapse,
  CircularProgress,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import {
  Backspace,
  Call,
  CallEnd,
  Dialpad as DialpadIcon,
  Mic,
  MicOff,
} from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import InfoCard from "components/detail/InfoCard";
import MonoText from "components/MonoText";
import StatusBadge from "components/StatusBadge";
import colors from "styles/tokens/colors";
import { CALL_STATE, CALL_STATE_META, formatDuration } from "telephony/callStates";
import { Dialpad, RoundButton } from "telephony/components";
import LinkRecordPanel from "./LinkRecordPanel";
import RecordDialSearch from "./RecordDialSearch";
import FollowUpFields from "./FollowUpFields";

const cc = colors.callCenter;

/** Idle: compose a number and dial it, or find who to dial by name. */
function IdlePanel({
  number,
  onNumberChange,
  onDial,
  onDialRecord,
  onLookup,
  canDial,
  dialDisabledReason,
}) {
  const [padOpen, setPadOpen] = useState(false);

  return (
    <InfoCard icon="dialpad" title="Place a call">
      <MDBox display="flex" flexDirection="column" gap={2}>
        <MDBox display="flex" alignItems="center" gap={1}>
          <TextField
            fullWidth
            size="small"
            label="Phone number"
            placeholder="+971 50 123 4567"
            value={number}
            onChange={(e) => onNumberChange(e.target.value)}
            onBlur={() => number.trim() && onLookup(number.trim())}
            onKeyDown={(e) => e.key === "Enter" && onDial()}
          />
          <RoundButton
            icon={<Backspace fontSize="small" />}
            onClick={() => onNumberChange(number.slice(0, -1))}
            disabled={!number}
            size={36}
            label=""
          />
        </MDBox>

        <MDButton
          variant="text"
          color="info"
          size="small"
          startIcon={<DialpadIcon />}
          onClick={() => setPadOpen((p) => !p)}
          sx={{ alignSelf: "flex-start", px: 0 }}
        >
          {padOpen ? "Hide keypad" : "Show keypad"}
        </MDButton>

        <Collapse in={padOpen}>
          <Dialpad onKey={(k) => onNumberChange(number + k)} />
        </Collapse>

        <MDButton
          variant="gradient"
          color="info"
          fullWidth
          startIcon={<Call />}
          disabled={!number.trim() || !canDial}
          onClick={onDial}
        >
          Call
        </MDButton>

        {!canDial ? (
          <MDTypography variant="caption" color="text">
            {dialDisabledReason || "The softphone is active in another browser tab."}
          </MDTypography>
        ) : null}

        <MDBox sx={{ borderTop: `1px solid ${cc.cardBorder}`, pt: 2 }}>
          <RecordDialSearch
            onDial={onDialRecord}
            canDial={canDial}
            disabledReason={dialDisabledReason}
          />
        </MDBox>
      </MDBox>
    </InfoCard>
  );
}

/** Who is on the line, in every non-idle state. */
function CallerCard({ meta, name, number, direction, durationSec, showTimer }) {
  return (
    <InfoCard icon="phone_in_talk" title={meta.label}>
      <MDBox display="flex" flexDirection="column" gap={1.5}>
        <MDBox>
          <MDTypography variant="h6" fontWeight="bold" color="dark">
            {name || "Unknown caller"}
          </MDTypography>
          {number ? <MonoText variant="button">{number}</MonoText> : null}
        </MDBox>
        <MDBox display="flex" alignItems="center" gap={1}>
          <StatusBadge status={meta.status} label={meta.label} size="sm" />
          <MDTypography variant="caption" color="text">
            {direction === "outbound" ? "Outbound" : "Inbound"}
          </MDTypography>
        </MDBox>
        {showTimer ? <MonoText variant="h4">{formatDuration(durationSec)}</MonoText> : null}
      </MDBox>
    </InfoCard>
  );
}

export default function CallPanel({
  callState,
  activeCall,
  counterparty,
  resolvedName,
  durationSec,
  muted,
  manualNumber,
  onManualNumberChange,
  onDial,
  onDialRecord,
  canDial,
  dialDisabledReason,
  onLookup,
  onAccept,
  onReject,
  onHangUp,
  onToggleMute,
  onSendDigits,
  dispositions,
  dispositionId,
  onDispositionChange,
  notes,
  onNotesChange,
  onNotesBlur,
  onComplete,
  onSkipWrapUp,
  saving,
  onLinked,
  followUp,
  onFollowUpChange,
  users,
  currentUserId,
  followUpError,
  unattached,
}) {
  const [padOpen, setPadOpen] = useState(false);

  const meta = CALL_STATE_META[callState] || CALL_STATE_META[CALL_STATE.IDLE];
  const isRinging = callState === CALL_STATE.RINGING;
  const isLive =
    callState === CALL_STATE.CONNECTED || callState === CALL_STATE.ON_HOLD;
  const isWrapUp = callState === CALL_STATE.WRAP_UP;
  const isConnecting = callState === CALL_STATE.CONNECTING;

  if (callState === CALL_STATE.IDLE) {
    return (
      <IdlePanel
        number={manualNumber}
        onNumberChange={onManualNumberChange}
        onDial={onDial}
        onDialRecord={onDialRecord}
        onLookup={onLookup}
        canDial={canDial}
        dialDisabledReason={dialDisabledReason}
      />
    );
  }

  const selected = dispositions.find((d) => d.id === dispositionId);
  const noteRequired = Boolean(selected?.requires_notes) && !notes.trim();

  return (
    <MDBox display="flex" flexDirection="column" gap={2}>
      <CallerCard
        meta={meta}
        name={resolvedName}
        number={counterparty}
        direction={activeCall?.direction}
        durationSec={durationSec}
        showTimer={isLive || isWrapUp}
      />

      {/*
        What the SEEP AI assistant learned before handing the caller over.
        Shown from the first ring, so the agent reads it before saying hello
        and the caller does not have to repeat themselves.
      */}
      {activeCall?.ai_summary ? (
        <InfoCard icon="support_agent" title="From the AI assistant">
          <MDTypography variant="button" color="text" sx={{ whiteSpace: "pre-line" }}>
            {activeCall.ai_summary}
          </MDTypography>
        </InfoCard>
      ) : null}

      {isRinging ? (
        <InfoCard icon="ring_volume" title="Incoming">
          <MDBox display="flex" justifyContent="space-around">
            <RoundButton icon={<Call />} label="Accept" onClick={onAccept} active />
            <RoundButton icon={<CallEnd />} label="Decline" onClick={onReject} danger />
          </MDBox>
        </InfoCard>
      ) : null}

      {isConnecting ? (
        <InfoCard icon="settings_phone" title="Connecting">
          <MDBox display="flex" alignItems="center" gap={1.5}>
            <CircularProgress size={18} />
            <MDTypography variant="button" color="text">
              Waiting for the other end to pick up.
            </MDTypography>
          </MDBox>
        </InfoCard>
      ) : null}

      {isLive ? (
        <InfoCard icon="settings_phone" title="Controls">
          <MDBox display="flex" justifyContent="space-around">
            <RoundButton
              icon={muted ? <MicOff /> : <Mic />}
              label={muted ? "Unmute" : "Mute"}
              onClick={onToggleMute}
              active={muted}
            />
            <RoundButton
              icon={<DialpadIcon />}
              label="Keypad"
              onClick={() => setPadOpen((p) => !p)}
              active={padOpen}
            />
            <RoundButton icon={<CallEnd />} label="Hang up" onClick={onHangUp} danger />
          </MDBox>
          <Collapse in={padOpen}>
            <MDBox mt={2}>
              {/* Tones, not composition: these go down the live line. */}
              <Dialpad onKey={onSendDigits} showLetters={false} size="small" />
            </MDBox>
          </Collapse>
        </InfoCard>
      ) : null}

      {/*
        Notes are offered during the call as well as after it, because an
        agent types while they listen. The disposition and the Save button are
        genuinely disabled until wrap-up rather than merely dimmed, so
        keyboard users cannot reach a control that does nothing.
      */}
      {isLive || isWrapUp ? (
        <InfoCard icon="assignment_turned_in" title="Wrap-up">
          <MDBox display="flex" flexDirection="column" gap={2}>
            <Select
              size="small"
              displayEmpty
              value={dispositionId}
              disabled={!isWrapUp}
              onChange={(e) => onDispositionChange(e.target.value)}
              inputProps={{ "aria-label": "Call outcome" }}
            >
              <MenuItem value="">
                <em>Select an outcome</em>
              </MenuItem>
              {dispositions.map((d) => (
                <MenuItem key={d.id} value={d.id}>
                  {d.name}
                </MenuItem>
              ))}
            </Select>

            <TextField
              multiline
              rows={4}
              size="small"
              label="Notes"
              value={notes}
              onChange={(e) => onNotesChange(e.target.value)}
              onBlur={onNotesBlur}
              error={noteRequired && isWrapUp}
              helperText={
                selected?.requires_notes
                  ? "This outcome requires a note."
                  : isLive
                  ? "Saved as you go. You can finish after the call."
                  : " "
              }
            />

            {/* Attaching the call sits above Save deliberately: once the
                agent clicks Save the call is gone from the console, and an
                unattached call is one nobody ever goes back to attach. */}
            <MDBox
              sx={{ borderTop: `1px solid ${cc.cardBorder}`, pt: 2 }}
            >
              <MDTypography
                variant="caption"
                fontWeight="medium"
                color="text"
                display="block"
                mb={1}
              >
                Who was this call about?
              </MDTypography>
              <LinkRecordPanel
                conversation={activeCall}
                onLinked={onLinked}
                disabled={!activeCall}
              />
              {unattached && isWrapUp ? (
                <MDTypography variant="caption" display="block" mt={1} sx={{ color: "#8A4B08" }}>
                  Not attached to a record yet, so the outcome won&apos;t appear on anyone&apos;s timeline.
                </MDTypography>
              ) : null}
            </MDBox>

            {isWrapUp && followUp && onFollowUpChange ? (
              <MDBox sx={{ borderTop: `1px solid ${cc.cardBorder}`, pt: 2 }}>
                <FollowUpFields
                  value={followUp}
                  onChange={onFollowUpChange}
                  users={users}
                  currentUserId={currentUserId}
                  disabled={saving}
                  problem={followUpError}
                />
              </MDBox>
            ) : null}

            <MDButton
              variant="gradient"
              color="info"
              fullWidth
              disabled={saving || !isWrapUp || noteRequired || Boolean(followUpError)}
              onClick={onComplete}
            >
              {saving ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                "Save & complete"
              )}
            </MDButton>

            {/* Wrap-up is a grace period, not a gate. An agent who has nothing
                to add should be able to take the next call now instead of
                waiting out the timer -- the call stays dispositionable from its
                own record, and the notes typed during the call are kept. */}
            {isWrapUp ? (
              <MDButton
                variant="text"
                color="info"
                fullWidth
                size="small"
                disabled={saving}
                onClick={onSkipWrapUp}
              >
                Skip wrap-up
              </MDButton>
            ) : null}

            {isLive ? (
              <MDTypography variant="caption" color="text" sx={{ color: cc.muted }}>
                The outcome can be set once the call ends.
              </MDTypography>
            ) : null}
          </MDBox>
        </InfoCard>
      ) : null}
    </MDBox>
  );
}
